import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, MessageSquare, Heart, BarChart3, Settings, Edit3, RefreshCw } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import { useRole } from "@/hooks/useRole";
import { fetchMentorCommunityById, fetchCommunityStats } from "@/services/mentorCommunityService";
import { CommunityWithDetails } from "@/types/community";

const DashboardComunidade = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { role, user, loading: roleLoading } = useRole();
  const { toast } = useToast();

  console.log('📊 [DashboardComunidade] ID:', id);

  const [community, setCommunity] = useState<CommunityWithDetails | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    console.log('📊 [DashboardComunidade] useEffect disparado:', { 
      id, 
      userId: user?.id, 
      role, 
      roleLoading,
      hasUser: !!user 
    });
    
    // Aguardar carregamento completo do role/user
    if (roleLoading) {
      console.log('📊 [DashboardComunidade] Aguardando carregamento do role...');
      return;
    }

    if (!user) {
      console.log('📊 [DashboardComunidade] Nenhum usuário logado, redirecionando...');
      navigate('/');
      return;
    }

    if (role !== 'mentor') {
      console.log('📊 [DashboardComunidade] Role não é mentor, redirecionando para home. Role atual:', role);
      navigate('/');
      return;
    }

    if (id && user?.id) {
      console.log('📊 [DashboardComunidade] Carregando dados da comunidade...');
      loadData();
    } else {
      console.log('📊 [DashboardComunidade] Parâmetros faltando:', { id, userId: user?.id });
    }
  }, [id, user, role, roleLoading, navigate]);

  const loadData = async () => {
    if (!id || !user?.id) return;

    try {
      setLoading(true);
      
      const communityData = await fetchMentorCommunityById(id, user.id);

      if (!communityData) {
        toast({
          title: "Erro",
          description: "Comunidade não encontrada ou você não tem permissão para acessá-la",
          variant: "destructive",
        });
        navigate('/painel-mentor/comunidades');
        return;
      }

      setCommunity(communityData);

      // Tentar buscar estatísticas (pode falhar se não existir ainda)
      try {
        const statsData = await fetchCommunityStats(id, user.id);
        setStats(statsData);
      } catch (error) {
        console.log('Estatísticas não disponíveis ainda');
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Falha ao carregar dados da comunidade",
        variant: "destructive",
      });
      navigate('/painel-mentor/comunidades');
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = async () => {
    if (!id || !user?.id) return;

    try {
      setRefreshing(true);
      const statsData = await fetchCommunityStats(id, user.id);
      setStats(statsData);
      
      toast({
        title: "Atualizado!",
        description: "Estatísticas atualizadas com sucesso",
      });
    } catch (error) {
      console.error('Erro ao atualizar estatísticas:', error);
      toast({
        title: "Erro",
        description: "Falha ao atualizar estatísticas",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!community) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Comunidade não encontrada</h1>
          <Button onClick={() => navigate('/painel-mentor/comunidades')}>
            Voltar para Comunidades
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/painel-mentor/comunidades')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white">
                Dashboard - {community.name}
              </h1>
              <p className="text-gray-400">
                {community.games?.name}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={refreshStats}
              disabled={refreshing}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Atualizando...' : 'Atualizar'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/comunidades/${community.slug}`)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Ver Comunidade
            </Button>
            <Button
              onClick={() => navigate(`/painel-mentor/comunidades/${community.id}/editar`)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </div>
        </div>

        {/* Estatísticas Principais */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Membros</p>
                  <p className="text-2xl font-bold text-white">
                    {stats?.memberCount || community.member_count || 'N/A'}
                  </p>
                </div>
                <div className="bg-blue-600 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Posts</p>
                  <p className="text-2xl font-bold text-white">
                    {stats?.postCount || community.post_count || 0}
                  </p>
                </div>
                <div className="bg-green-600 p-3 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Likes</p>
                  <p className="text-2xl font-bold text-white">
                    {stats?.likeCount || community.like_count || 0}
                  </p>
                </div>
                <div className="bg-red-600 p-3 rounded-lg">
                  <Heart className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Comentários</p>
                  <p className="text-2xl font-bold text-white">
                    {stats?.commentCount || community.comment_count || 0}
                  </p>
                </div>
                <div className="bg-purple-600 p-3 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Informações da Comunidade */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Informações Gerais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-gray-400 text-sm">Nome</p>
                <p className="text-white">{community.name}</p>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm">URL</p>
                <p className="text-white">/comunidades/{community.slug}</p>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm">Jogo</p>
                <p className="text-white">{community.games?.name}</p>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm">Status</p>
                <p className={`font-medium ${community.is_active ? 'text-green-400' : 'text-red-400'}`}>
                  {community.is_active ? 'Ativa' : 'Inativa'}
                </p>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm">Criada em</p>
                <p className="text-white">
                  {new Date(community.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Descrição</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-300 leading-relaxed">
                {community.description || 'Nenhuma descrição disponível.'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Membros Recentes */}
        {stats?.recentMembers && stats.recentMembers.length > 0 && (
          <Card className="bg-gray-800 border-gray-700 mt-6">
            <CardHeader>
              <CardTitle className="text-white">Membros Recentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recentMembers.map((member: any, index: number) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-gray-300" />
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {member.profiles?.full_name || 'Usuário'}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {new Date(member.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Posts Populares */}
        {stats?.popularPosts && stats.popularPosts.length > 0 && (
          <Card className="bg-gray-800 border-gray-700 mt-6">
            <CardHeader>
              <CardTitle className="text-white">Posts Populares</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.popularPosts.map((post: any) => (
                  <div key={post.id} className="border-b border-gray-700 pb-3 last:border-0">
                    <h4 className="text-white font-medium">{post.title}</h4>
                    <div className="flex items-center gap-4 mt-1 text-gray-400 text-sm">
                      <span>{post.like_count} likes</span>
                      <span>{post.comment_count} comentários</span>
                      <span>{new Date(post.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DashboardComunidade; 